import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Cake, Save, RotateCcw } from "lucide-react";

/**
 * AdminBirthdayRewardsTab — manage the birthday reward feature.
 *  - Toggle on/off
 *  - Set amount (fixed $ or %) + expiration window
 *  - Browse issued coupons
 */
export default function AdminBirthdayRewardsTab() {
  const [settings, setSettings] = useState(null);
  const [issued, setIssued] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const loadSettings = () =>
    api
      .get("/admin/birthday-rewards/settings")
      .then((r) => setSettings(r.data))
      .catch(() => setSettings(null));
  const loadIssued = () =>
    api
      .get("/admin/birthday-rewards/issued")
      .then((r) => setIssued(r.data.items || []))
      .catch(() => setIssued([]));

  useEffect(() => {
    loadSettings();
    loadIssued();
  }, []);

  if (!settings) return <p data-testid="admin-bday-loading">Loading…</p>;

  const save = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.put("/admin/birthday-rewards/settings", settings);
      setSettings(data);
      setMsg("Saved.");
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-bday-tab">
      <div className="flex items-center gap-3">
        <Cake size={22} className="text-[var(--ff-secondary)]" />
        <div>
          <p className="overline">Member perks</p>
          <h2 className="font-display text-3xl">Birthday Rewards</h2>
        </div>
      </div>

      <div className="card-flat p-6 space-y-6" data-testid="admin-bday-settings">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            className="w-5 h-5"
            data-testid="bday-enabled"
          />
          <span className="font-semibold">
            Birthday rewards are{" "}
            <span className={settings.enabled ? "text-[var(--ff-primary)]" : "text-rose-700"}>
              {settings.enabled ? "ON" : "OFF"}
            </span>
          </span>
        </label>

        <div className="grid sm:grid-cols-3 gap-5">
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--ff-text-muted)] block mb-1.5">
              Reward type
            </label>
            <select
              className="input-base w-full"
              value={settings.discount_type}
              onChange={(e) => setSettings({ ...settings, discount_type: e.target.value })}
              data-testid="bday-type"
            >
              <option value="fixed">$ off</option>
              <option value="percent">% off</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--ff-text-muted)] block mb-1.5">
              Reward amount
            </label>
            <input
              type="number"
              min="1"
              step="1"
              className="input-base w-full"
              value={settings.discount_value}
              onChange={(e) =>
                setSettings({ ...settings, discount_value: parseFloat(e.target.value) || 0 })
              }
              data-testid="bday-value"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--ff-text-muted)] block mb-1.5">
              Expires (days)
            </label>
            <input
              type="number"
              min="1"
              max="365"
              className="input-base w-full"
              value={settings.expires_days}
              onChange={(e) =>
                setSettings({ ...settings, expires_days: parseInt(e.target.value, 10) || 1 })
              }
              data-testid="bday-expires"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="bday-save"
          >
            <Save size={14} /> {busy ? "Saving…" : "Save settings"}
          </button>
          <button
            type="button"
            onClick={loadSettings}
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="bday-reload"
          >
            <RotateCcw size={14} /> Reload
          </button>
          {msg && (
            <span className="text-sm text-[var(--ff-primary)]" data-testid="bday-msg">
              {msg}
            </span>
          )}
        </div>
      </div>

      <div className="card-flat overflow-hidden" data-testid="admin-bday-issued">
        <div className="p-5 border-b border-[var(--ff-border)] flex items-center justify-between">
          <h3 className="font-display text-xl">Issued coupons</h3>
          <span className="text-sm text-[var(--ff-text-muted)]">
            {issued.length} total
          </span>
        </div>
        {issued.length === 0 ? (
          <p className="p-8 text-center text-[var(--ff-text-muted)]">
            No birthday coupons have been issued yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--ff-muted)] text-left">
                <tr>
                  <th className="px-5 py-3 font-semibold">Member</th>
                  <th className="px-5 py-3 font-semibold">Code</th>
                  <th className="px-5 py-3 font-semibold">Reward</th>
                  <th className="px-5 py-3 font-semibold">Issued</th>
                  <th className="px-5 py-3 font-semibold">Expires</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {issued.map((c) => (
                  <tr key={c.id} className="border-t border-[var(--ff-border)]" data-testid={`bday-row-${c.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium">{c.user_name || "—"}</div>
                      <div className="text-xs text-[var(--ff-text-muted)]">{c.user_email}</div>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs">{c.code}</td>
                    <td className="px-5 py-3">
                      {c.discount_type === "percent"
                        ? `${c.discount_value}%`
                        : `$${c.discount_value}`}
                    </td>
                    <td className="px-5 py-3 text-xs">
                      {new Date(c.issued_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3 text-xs">
                      {new Date(c.expires_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3">
                      {c.redeemed ? (
                        <span className="text-[var(--ff-text-muted)]">Redeemed</span>
                      ) : c.email_sent ? (
                        <span className="text-[var(--ff-primary)]">Active · Emailed</span>
                      ) : (
                        <span className="text-[var(--ff-secondary)]">Active</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-[var(--ff-text-muted)] max-w-2xl italic">
        Birthday coupons are issued automatically when a member first loads
        their dashboard during their birthday month. Each member receives at
        most one coupon per calendar year.
      </p>
    </div>
  );
}
